/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  BEResponsePagedResponseNotificaUtenteResponseDTO,
  GetFrontNotificheParams,
  NotificaUtenteRequestDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Creazione notifica
 * @summary Crea Notifica
 */
export const putFrontNotificheCrea = (
  notificaUtenteRequestDTO: NotificaUtenteRequestDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/notifiche/crea`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: notificaUtenteRequestDTO,
    },
    options,
  );
};

export const getPutFrontNotificheCreaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontNotificheCrea>>,
    TError,
    { data: NotificaUtenteRequestDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontNotificheCrea>>,
  TError,
  { data: NotificaUtenteRequestDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontNotificheCrea>>,
    { data: NotificaUtenteRequestDTO }
  > = props => {
    const { data } = props ?? {};

    return putFrontNotificheCrea(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontNotificheCreaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontNotificheCrea>>
>;
export type PutFrontNotificheCreaMutationBody = NotificaUtenteRequestDTO;
export type PutFrontNotificheCreaMutationError = ErrorType<unknown>;

/**
 * @summary Crea Notifica
 */
export const usePutFrontNotificheCrea = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontNotificheCrea>>,
    TError,
    { data: NotificaUtenteRequestDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutFrontNotificheCreaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Registrazione dell'avvenuta lettura della notifica
 * @summary Registrazione lettura notifica
 */
export const postFrontNotificheIdPresaVisione = (
  idNotifica: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<boolean>(
    {
      url: `/framework-cmp-domande/notifiche/${idNotifica}/presa-visione`,
      method: 'post',
    },
    options,
  );
};

export const getPostFrontNotificheIdPresaVisioneMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontNotificheIdPresaVisione>>,
    TError,
    { idNotifica: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontNotificheIdPresaVisione>>,
  TError,
  { idNotifica: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontNotificheIdPresaVisione>>,
    { idNotifica: string }
  > = props => {
    const { idNotifica } = props ?? {};

    return postFrontNotificheIdPresaVisione(idNotifica, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontNotificheIdPresaVisioneMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontNotificheIdPresaVisione>>
>;

export type PostFrontNotificheIdPresaVisioneMutationError = ErrorType<unknown>;

/**
 * @summary Registrazione lettura notifica
 */
export const usePostFrontNotificheIdPresaVisione = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontNotificheIdPresaVisione>>,
    TError,
    { idNotifica: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontNotificheIdPresaVisioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Lista Notifiche
 * @summary Elenco Notifiche
 */
export const getFrontNotifiche = (
  params?: GetFrontNotificheParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponsePagedResponseNotificaUtenteResponseDTO>(
    { url: `/framework-cmp-domande/notifiche`, method: 'get', params, signal },
    options,
  );
};

export const getGetFrontNotificheQueryKey = (
  params?: GetFrontNotificheParams,
) => [`/framework-cmp-domande/notifiche`, ...(params ? [params] : [])] as const;

export const getGetFrontNotificheQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontNotifiche>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontNotificheParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontNotifiche>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontNotifiche>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontNotificheQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontNotifiche>>
  > = ({ signal }) => getFrontNotifiche(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontNotificheQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontNotifiche>>
>;
export type GetFrontNotificheQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Notifiche
 */
export const useGetFrontNotifiche = <
  TData = Awaited<ReturnType<typeof getFrontNotifiche>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontNotificheParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontNotifiche>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontNotificheQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
